/**
 * ConnectedAccountsSection Component
 * Manage connected OAuth accounts (Google, Apple, etc.)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { AppleIcon, GoogleIcon, InstagramIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { useUpdateUserSettings } from "../hooks/useSettings"
import {
  disconnectOAuthAccount,
  getConnectedOAuthAccounts,
  getOAuthUrl,
  triggerOAuthSync,
} from "../services/settings.service"
import type { ConnectedOAuthAccount, OAuthProvider } from "../types/settings.types"

const PROVIDER_CONFIG: Record<OAuthProvider, { label: string; icon: React.ReactNode }> = {
  google: {
    label: "Google",
    icon: <GoogleIcon className="size-6" />,
  },
  apple: {
    label: "Apple",
    icon: <AppleIcon className="size-6 fill-charcoal" />,
  },
  instagram: {
    label: "Instagram",
    icon: <InstagramIcon className="size-5" />,
  },
}

const SUPPORTED_PROVIDERS: OAuthProvider[] = ["google", "instagram"]

export function ConnectedAccountsSection() {
  const { t } = useTranslation("settings")
  const queryClient = useQueryClient()
  const user = useAuthStore(state => state.user)
  const [localAccounts, setLocalAccounts] = useState<
    Array<{
      provider: OAuthProvider
      connected: boolean
      account?: ConnectedOAuthAccount
      originalProvider: string
    }>
  >([])

  // Permission modal state
  const [showPermissionModal, setShowPermissionModal] = useState(false)
  const [pendingProvider, setPendingProvider] = useState<OAuthProvider | null>(null)
  const [modalCalendarEnabled, setModalCalendarEnabled] = useState(true)
  const [modalContactsEnabled, setModalContactsEnabled] = useState(true)

  // Settings mutation
  const updateSettingsMutation = useUpdateUserSettings()

  // Fetch connected accounts
  const { data: accountsData, isLoading } = useQuery({
    queryKey: ["connected-oauth-accounts"],
    queryFn: getConnectedOAuthAccounts,
  })

  // Initialize local state with available providers
  useEffect(() => {
    const connectedProviders = new Set(
      (accountsData?.accounts || []).map(a =>
        a.provider === "facebook" ? "instagram" : a.provider,
      ),
    )

    setLocalAccounts(
      SUPPORTED_PROVIDERS.map(provider => ({
        provider,
        connected: connectedProviders.has(provider),
        account: accountsData?.accounts?.find(
          a => a.provider === provider || (provider === "instagram" && a.provider === "facebook"),
        ),
        originalProvider:
          accountsData?.accounts?.find(
            a => a.provider === provider || (provider === "instagram" && a.provider === "facebook"),
          )?.provider || provider,
      })),
    )
  }, [accountsData])

  // Connect mutation - initiates OAuth flow
  const connectMutation = useMutation({
    mutationFn: async (provider: OAuthProvider) => {
      const { authUrl } = await getOAuthUrl(provider)
      // Redirect to OAuth URL
      window.open(authUrl, "_blank", "noopener,noreferrer")
    },
    onError: error => {
      toast.error(t("connectedAccounts.errorInitiateOAuth"))
      console.error("OAuth error:", error)
    },
  })

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: disconnectOAuthAccount,
    onSuccess: () => {
      toast.success(t("connectedAccounts.disconnectSuccess"))
      queryClient.invalidateQueries({ queryKey: ["connected-oauth-accounts"] })
    },
    onError: error => {
      toast.error(t("connectedAccounts.errorDisconnect"))
      console.error("Disconnect error:", error)
    },
  })

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () => triggerOAuthSync("google"),
    onSuccess: () => {
      toast.success(t("connectedAccounts.syncStarted"))
    },
    onError: error => {
      toast.error(t("connectedAccounts.errorSync"))
      console.error("Sync error:", error)
    },
  })

  const handleConnect = (provider: OAuthProvider) => {
    // For Google, check if calendar and contacts permissions are enabled
    if (provider === "google") {
      const settings = user?.userSettings
      if (!settings?.calendarEnabled || !settings?.contactsEnabled) {
        // Initialize modal state with current settings
        setModalCalendarEnabled(settings?.calendarEnabled ?? true)
        setModalContactsEnabled(settings?.contactsEnabled ?? true)
        setPendingProvider(provider)
        setShowPermissionModal(true)
        return
      }
    }
    // Proceed directly with OAuth
    connectMutation.mutate(provider)
  }

  const handlePermissionModalConfirm = async () => {
    // Save settings first
    try {
      await updateSettingsMutation.mutateAsync({
        calendarEnabled: modalCalendarEnabled,
        contactsEnabled: modalContactsEnabled,
      })
    } catch {
      // Error is handled in the mutation
      return
    }
    setShowPermissionModal(false)
    // Proceed with OAuth
    if (pendingProvider) {
      connectMutation.mutate(pendingProvider)
    }
  }

  const handlePermissionModalClose = () => {
    setShowPermissionModal(false)
    setPendingProvider(null)
  }

  const handleDisconnect = (provider: string) => {
    disconnectMutation.mutate(provider)
  }

  // Separate providers into connected and not connected
  const connectedAccounts = localAccounts.filter(a => a.connected)
  const notConnectedAccounts = localAccounts.filter(a => !a.connected)

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Section Header */}
      <div>
        <h1 className="text-lg md:text-xl leading-5 font-semibold text-primary uppercase section-heading">
          {t("connectedAccounts.title")}
        </h1>
        <p className="text-sm text-primary mt-2.5 md:mt-1.25">
          {t("connectedAccounts.description")}
        </p>
      </div>

      <div className="flex flex-col gap-7.5">
        {/* Not Connected Accounts */}
        {notConnectedAccounts.length > 0 && (
          <div className="flex flex-col gap-5">
            <h2 className="text-[15px] font-semibold text-primary leading-6.25">
              {t("connectedAccounts.connect")}
            </h2>
            {notConnectedAccounts.map(account => {
              const config = PROVIDER_CONFIG[account.provider]

              return (
                <div
                  key={account.provider}
                  className="flex flex-col md:flex-row md:items-center justify-between gap-3.75"
                >
                  <div className="flex items-center gap-3.75">
                    <span className="bg-card p-3 rounded-md">{config.icon}</span>
                    <div className="flex flex-col gap-1.25">
                      <span className="text-sm font-medium text-primary">{config.label}</span>
                      <span className="text-[13px] leading-5 text-primary">
                        {account.provider === "google"
                          ? t("connectedAccounts.googleDescription")
                          : t("connectedAccounts.socialDescription")}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="py-2.5 w-[140px] h-[40px] text-sm font-semibold"
                      onClick={() => handleConnect(account.provider)}
                      disabled={connectMutation.isPending || isLoading}
                    >
                      {connectMutation.isPending && connectMutation.variables === account.provider
                        ? t("connectedAccounts.connecting")
                        : t("connectedAccounts.connect")}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Connected Accounts */}
        {connectedAccounts.length > 0 && (
          <div className="flex flex-col gap-5">
            <h2 className="text-[15px] font-semibold text-primary leading-6.25">
              {t("connectedAccounts.connected")}
            </h2>
            {connectedAccounts.map(account => {
              const config = PROVIDER_CONFIG[account.provider]
              const accountInfo = account.account

              return (
                <div
                  key={account.provider}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-3.75"
                >
                  <div className="flex items-center gap-3.75">
                    <span className="bg-card p-3 rounded-md">{config.icon}</span>
                    <div className="flex flex-col gap-1.25">
                      <span className="text-sm font-medium text-primary">{config.label}</span>
                      <span className="text-[13px] leading-5 text-primary">
                        {account.provider === "google"
                          ? t("connectedAccounts.googleDescription")
                          : accountInfo?.name
                            ? t("connectedAccounts.loggedInAs", { name: accountInfo.name })
                            : accountInfo?.email || t("connectedAccounts.connected")}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-row lg:flex-col xl:flex-row items-center gap-2.5">
                    {account.provider === "google" && (
                      <Button
                        variant="outline"
                        className="py-2.5 w-[140px] h-[40px] text-sm font-semibold"
                        size="sm"
                        onClick={() => syncMutation.mutate()}
                        disabled={syncMutation.isPending}
                      >
                        {syncMutation.isPending
                          ? t("connectedAccounts.syncing")
                          : t("connectedAccounts.syncNow")}
                      </Button>
                    )}
                    <Button
                      variant="primary-terracotta"
                      className="py-2.5 w-[140px] h-[40px] text-sm font-semibold"
                      size="sm"
                      onClick={() => handleDisconnect(account.originalProvider)}
                      disabled={disconnectMutation.isPending}
                    >
                      {disconnectMutation.isPending &&
                      disconnectMutation.variables === account.originalProvider
                        ? t("connectedAccounts.disconnecting")
                        : t("connectedAccounts.disconnect")}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Permission Required Modal */}
      <Dialog
        open={showPermissionModal}
        onOpenChange={open => !open && handlePermissionModalClose()}
      >
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>{t("connectedAccounts.enablePermissions")}</DialogTitle>
            <DialogDescription>
              {t("connectedAccounts.enablePermissionsDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-charcoal">
                  {t("connectedAccounts.calendarAccess")}
                </span>
                <span className="text-xs text-stone">
                  {t("connectedAccounts.calendarDescription")}
                </span>
              </div>
              <Switch checked={modalCalendarEnabled} onCheckedChange={setModalCalendarEnabled} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-charcoal">
                  {t("connectedAccounts.contactsAccess")}
                </span>
                <span className="text-xs text-stone">
                  {t("connectedAccounts.contactsDescription")}
                </span>
              </div>
              <Switch checked={modalContactsEnabled} onCheckedChange={setModalContactsEnabled} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handlePermissionModalClose} className="px-15">
              {t("connectedAccounts.cancel")}
            </Button>
            <Button
              variant="primary"
              className="px-10"
              onClick={handlePermissionModalConfirm}
              disabled={
                !modalCalendarEnabled || !modalContactsEnabled || updateSettingsMutation.isPending
              }
            >
              {updateSettingsMutation.isPending
                ? t("connectedAccounts.saving")
                : t("connectedAccounts.continueButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
